import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Plus, ClipboardList, Loader2, Eye, BarChart3, Power, Trash2,
  CheckCircle, XCircle, Star, MessageSquare, ListChecks, Clock,
  Users, Building, ChevronDown, ChevronUp, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple Choice', icon: ListChecks },
  { value: 'free_text', label: 'Free Text', icon: MessageSquare },
  { value: 'rating', label: 'Rating (1-5)', icon: Star },
  { value: 'yes_no', label: 'Yes / No', icon: CheckCircle },
];

export default function Surveys() {
  const { getAuthHeaders } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', scheduled_time: '09:00',
    target_offices: ['all'], questions: [],
  });

  // View Responses / Analytics dialog
  const [viewSurvey, setViewSurvey] = useState(null);
  const [viewMode, setViewMode] = useState('responses'); // 'responses' | 'analytics'
  const [responseData, setResponseData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchSurveys = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/surveys`, { headers: getAuthHeaders() });
      if (res.ok) setSurveys(await res.json());
      else console.error('Failed to fetch surveys:', res.status);
    } catch (e) { console.error('Surveys fetch error:', e); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  const fetchOffices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/offices`, { headers: getAuthHeaders() });
      if (res.ok) setOffices(await res.json());
    } catch (e) { console.error(e); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchSurveys(); fetchOffices(); }, [fetchSurveys, fetchOffices]);

  // ─── Create Survey ──────────────────────────────────────
  const addQuestion = () => {
    setForm(f => ({
      ...f,
      questions: [...f.questions, { text: '', type: 'multiple_choice', options: [''], required: true }],
    }));
  };

  const updateQuestion = (idx, field, value) => {
    setForm(f => {
      const qs = [...f.questions];
      qs[idx] = { ...qs[idx], [field]: value };
      return { ...f, questions: qs };
    });
  };

  const removeQuestion = (idx) => {
    setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  };

  const addOption = (qIdx) => {
    setForm(f => {
      const qs = [...f.questions];
      qs[qIdx] = { ...qs[qIdx], options: [...qs[qIdx].options, ''] };
      return { ...f, questions: qs };
    });
  };

  const updateOption = (qIdx, oIdx, value) => {
    setForm(f => {
      const qs = [...f.questions];
      const opts = [...qs[qIdx].options];
      opts[oIdx] = value;
      qs[qIdx] = { ...qs[qIdx], options: opts };
      return { ...f, questions: qs };
    });
  };

  const removeOption = (qIdx, oIdx) => {
    setForm(f => {
      const qs = [...f.questions];
      qs[qIdx] = { ...qs[qIdx], options: qs[qIdx].options.filter((_, i) => i !== oIdx) };
      return { ...f, questions: qs };
    });
  };

  const toggleOffice = (officeId) => {
    setForm(f => {
      let targets = [...f.target_offices];
      if (officeId === 'all') {
        targets = ['all'];
      } else {
        targets = targets.filter(t => t !== 'all');
        if (targets.includes(officeId)) {
          targets = targets.filter(t => t !== officeId);
          if (targets.length === 0) targets = ['all'];
        } else {
          targets.push(officeId);
        }
      }
      return { ...f, target_offices: targets };
    });
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (form.questions.length === 0) return toast.error('Add at least one question');
    for (const q of form.questions) {
      if (!q.text.trim()) return toast.error('All questions must have text');
      if (q.type === 'multiple_choice' && q.options.filter(o => o.trim()).length < 2) {
        return toast.error('Multiple choice needs at least 2 options');
      }
    }

    setSaving(true);
    try {
      const body = {
        ...form,
        questions: form.questions.map(q => ({
          ...q,
          options: q.type === 'multiple_choice' ? q.options.filter(o => o.trim()) : [],
        })),
      };
      const res = await fetch(`${API_URL}/api/surveys`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Survey created and pushed to employees');
        setShowCreate(false);
        setForm({ title: '', description: '', scheduled_time: '09:00', target_offices: ['all'], questions: [] });
        fetchSurveys();
      } else {
        let errMsg = `Server error (${res.status})`;
        try { const err = await res.json(); errMsg = err.detail || errMsg; } catch {}
        toast.error(errMsg);
      }
    } catch (e) {
      console.error('Survey creation failed:', e);
      toast.error(`Failed to create survey: ${e.message || 'Network error'}`);
    }
    finally { setSaving(false); }
  };

  // ─── Toggle Status ──────────────────────────────────────
  const toggleStatus = async (survey) => {
    const newStatus = survey.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${API_URL}/api/surveys/${survey.id}`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(`Survey ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchSurveys();
      }
    } catch (e) { toast.error('Failed to update'); }
  };

  // ─── View Responses / Analytics ─────────────────────────
  const openView = async (survey, mode) => {
    setViewSurvey(survey);
    setViewMode(mode);
    setViewLoading(true);
    setResponseData(null);
    setAnalyticsData(null);
    try {
      const headers = getAuthHeaders();
      if (mode === 'responses') {
        const res = await fetch(`${API_URL}/api/surveys/${survey.id}/responses`, { headers });
        if (res.ok) setResponseData(await res.json());
      } else {
        const res = await fetch(`${API_URL}/api/surveys/${survey.id}/analytics`, { headers });
        if (res.ok) setAnalyticsData(await res.json());
      }
    } catch (e) { console.error(e); }
    finally { setViewLoading(false); }
  };

  const filteredSurveys = activeTab === 'all' ? surveys
    : activeTab === 'active' ? surveys.filter(s => s.status === 'active')
    : surveys.filter(s => s.status === 'inactive');

  return (
    <div className="space-y-6" data-testid="surveys-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="surveys-title">Surveys</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage employee surveys</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-survey-btn" className="bg-teal-600 hover:bg-teal-700">
          <Plus className="h-4 w-4 mr-2" /> New Survey
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All ({surveys.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active ({surveys.filter(s => s.status === 'active').length})</TabsTrigger>
          <TabsTrigger value="inactive" data-testid="tab-inactive">Inactive ({surveys.filter(s => s.status !== 'active').length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>
      ) : filteredSurveys.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-gray-400">
          <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p>No surveys yet. Create your first survey to get started.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {filteredSurveys.map(survey => (
            <SurveyCard key={survey.id} survey={survey} offices={offices}
              onToggleStatus={() => toggleStatus(survey)}
              onViewResponses={() => openView(survey, 'responses')}
              onViewAnalytics={() => openView(survey, 'analytics')} />
          ))}
        </div>
      )}

      {/* Create Survey Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-teal-600" /> Create New Survey
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Title + Description */}
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Title *</label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Employee Satisfaction Survey" data-testid="survey-title-input" className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of the survey..." data-testid="survey-desc-input"
                  className="w-full mt-1 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[60px]" />
              </div>
            </div>

            {/* Scheduled Time */}
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> Popup Time
              </label>
              <p className="text-xs text-gray-400 mb-1">When the survey popup appears for employees</p>
              <Input type="time" value={form.scheduled_time}
                onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))}
                data-testid="survey-time-input" className="w-40" />
            </div>

            {/* Target Offices */}
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <Building className="h-3.5 w-3.5" /> Target Offices
              </label>
              <div className="flex flex-wrap gap-2 mt-2">
                <button type="button" onClick={() => toggleOffice('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    form.target_offices.includes('all') ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'
                  }`} data-testid="target-all-offices">
                  All Offices
                </button>
                {offices.map(o => (
                  <button key={o.id} type="button" onClick={() => toggleOffice(o.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      form.target_offices.includes(o.id) ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'
                    }`} data-testid={`target-office-${o.id}`}>
                    {o.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Questions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">Questions</label>
                <Button size="sm" variant="outline" onClick={addQuestion} data-testid="add-question-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Question
                </Button>
              </div>

              {form.questions.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6 border border-dashed rounded-lg">
                  No questions yet. Click "Add Question" to start.
                </p>
              )}

              <div className="space-y-4">
                {form.questions.map((q, qi) => (
                  <div key={qi} className="border rounded-lg p-4 bg-gray-50/50 space-y-3" data-testid={`question-${qi}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 space-y-2">
                        <Input value={q.text} onChange={e => updateQuestion(qi, 'text', e.target.value)}
                          placeholder={`Question ${qi + 1}`} data-testid={`question-text-${qi}`} />
                        <div className="flex flex-wrap gap-2">
                          {QUESTION_TYPES.map(t => (
                            <button key={t.value} type="button" onClick={() => updateQuestion(qi, 'type', t.value)}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                                q.type === t.value ? 'bg-teal-50 text-teal-700 border-teal-300' : 'bg-white text-gray-500 border-gray-200 hover:border-teal-300'
                              }`} data-testid={`q${qi}-type-${t.value}`}>
                              <t.icon className="h-3 w-3" /> {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeQuestion(qi)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Multiple choice options */}
                    {q.type === 'multiple_choice' && (
                      <div className="pl-2 space-y-2">
                        {q.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" />
                            <Input value={opt} onChange={e => updateOption(qi, oi, e.target.value)}
                              placeholder={`Option ${oi + 1}`} className="h-8 text-sm"
                              data-testid={`q${qi}-opt-${oi}`} />
                            {q.options.length > 1 && (
                              <Button size="sm" variant="ghost" onClick={() => removeOption(qi, oi)} className="h-8 w-8 p-0 text-gray-400">
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button size="sm" variant="ghost" onClick={() => addOption(qi)} className="text-teal-600 text-xs">
                          <Plus className="h-3 w-3 mr-1" /> Add Option
                        </Button>
                      </div>
                    )}

                    {/* Required toggle */}
                    <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                      <input type="checkbox" checked={q.required}
                        onChange={e => updateQuestion(qi, 'required', e.target.checked)}
                        className="rounded border-gray-300" />
                      Required
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="submit-survey-btn" className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Create & Push Survey
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Responses / Analytics Dialog */}
      <Dialog open={!!viewSurvey} onOpenChange={() => setViewSurvey(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewMode === 'responses' ? <Eye className="h-5 w-5 text-blue-600" /> : <BarChart3 className="h-5 w-5 text-violet-600" />}
              {viewSurvey?.title} - {viewMode === 'responses' ? 'Responses' : 'Analytics'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-2 mb-4">
            <Button size="sm" variant={viewMode === 'responses' ? 'default' : 'outline'}
              onClick={() => { if (viewSurvey) openView(viewSurvey, 'responses'); }}
              data-testid="view-responses-tab">
              <Eye className="h-3.5 w-3.5 mr-1" /> Responses
            </Button>
            <Button size="sm" variant={viewMode === 'analytics' ? 'default' : 'outline'}
              onClick={() => { if (viewSurvey) openView(viewSurvey, 'analytics'); }}
              data-testid="view-analytics-tab">
              <BarChart3 className="h-3.5 w-3.5 mr-1" /> Analytics
            </Button>
          </div>

          {viewLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
          ) : viewMode === 'responses' ? (
            <ResponsesView data={responseData} />
          ) : (
            <AnalyticsView data={analyticsData} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Survey Card ─────────────────────────────────────────────────────
function SurveyCard({ survey, offices, onToggleStatus, onViewResponses, onViewAnalytics }) {
  const targetLabel = survey.target_offices?.includes('all')
    ? 'All Offices'
    : survey.target_offices?.map(id => offices.find(o => o.id === id)?.name || id).join(', ');

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`survey-card-${survey.id}`}>
      <CardContent className="py-4 px-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 truncate">{survey.title}</h3>
              <Badge variant={survey.status === 'active' ? 'default' : 'secondary'}
                className={survey.status === 'active' ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}>
                {survey.status}
              </Badge>
            </div>
            {survey.description && <p className="text-sm text-gray-500 line-clamp-1 mb-2">{survey.description}</p>}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-400">
              <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" /> {survey.questions?.length || 0} questions</span>
              <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {survey.response_count || 0} responses</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Popup at {survey.scheduled_time}</span>
              <span className="flex items-center gap-1"><Building className="h-3 w-3" /> {targetLabel}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" variant="outline" onClick={onViewResponses} data-testid={`view-responses-${survey.id}`}>
              <Eye className="h-3.5 w-3.5 mr-1" /> Responses
            </Button>
            <Button size="sm" variant="outline" onClick={onViewAnalytics} data-testid={`view-analytics-${survey.id}`}>
              <BarChart3 className="h-3.5 w-3.5 mr-1" /> Analytics
            </Button>
            <Button size="sm" variant="ghost" onClick={onToggleStatus}
              data-testid={`toggle-status-${survey.id}`}
              className={survey.status === 'active' ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-700'}>
              <Power className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Responses View ──────────────────────────────────────────────────
function ResponsesView({ data }) {
  const [expanded, setExpanded] = useState(null);
  if (!data) return <p className="text-gray-400 text-center py-8">No data</p>;

  const { survey, responses } = data;
  const questions = survey?.questions || [];

  if (responses.length === 0) {
    return <p className="text-gray-400 text-center py-8">No responses yet</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-500 mb-3">{responses.length} response{responses.length !== 1 ? 's' : ''}</p>
      {responses.map((resp, ri) => (
        <div key={resp.id} className="border rounded-lg overflow-hidden" data-testid={`response-row-${ri}`}>
          <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setExpanded(prev => prev === ri ? null : ri)}>
            <div>
              <p className="font-medium text-sm text-gray-900">{resp.employee_name}</p>
              <p className="text-xs text-gray-400">{resp.office_name} &middot; {new Date(resp.submitted_at).toLocaleString()}</p>
            </div>
            {expanded === ri ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </div>
          {expanded === ri && (
            <div className="px-4 pb-4 space-y-3 bg-gray-50/50 border-t">
              {questions.map(q => {
                const ans = resp.answers?.find(a => a.question_id === q.id);
                return (
                  <div key={q.id} className="pt-2">
                    <p className="text-xs font-medium text-gray-600 mb-0.5">{q.text}</p>
                    <p className="text-sm text-gray-900">
                      {ans ? formatAnswer(ans.answer, q.type) : <span className="text-gray-400 italic">No answer</span>}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function formatAnswer(answer, type) {
  if (type === 'yes_no') return answer === true || String(answer).toLowerCase() === 'true' ? 'Yes' : 'No';
  if (type === 'rating') return `${'★'.repeat(Number(answer))}${'☆'.repeat(5 - Number(answer))} (${answer}/5)`;
  return String(answer);
}

// ─── Analytics View ──────────────────────────────────────────────────
function AnalyticsView({ data }) {
  if (!data) return <p className="text-gray-400 text-center py-8">No data</p>;

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{data.total_responses}</p>
            <p className="text-xs text-gray-500">Responses</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{data.targeted_employees}</p>
            <p className="text-xs text-gray-500">Targeted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-teal-600">{data.completion_rate}%</p>
            <p className="text-xs text-gray-500">Completion</p>
          </CardContent>
        </Card>
      </div>

      {/* Office breakdown */}
      {Object.keys(data.office_breakdown || {}).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Responses by Office</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.office_breakdown).map(([office, count]) => (
                <div key={office} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{office}</span>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-question analytics */}
      {data.question_analytics?.map((qa, i) => (
        <Card key={qa.question_id} data-testid={`analytics-q-${i}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="text-xs text-gray-400 font-normal">Q{i + 1}</span>
              {qa.text}
              <Badge variant="outline" className="ml-auto text-xs">{qa.total_answers} answers</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {qa.type === 'multiple_choice' && qa.option_counts && (
              <div className="space-y-2">
                {Object.entries(qa.option_counts).map(([opt, count]) => {
                  const pct = qa.total_answers > 0 ? Math.round((count / qa.total_answers) * 100) : 0;
                  return (
                    <div key={opt}>
                      <div className="flex items-center justify-between text-sm mb-0.5">
                        <span className="text-gray-700">{opt}</span>
                        <span className="text-gray-500">{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {qa.type === 'rating' && (
              <div className="space-y-2">
                <p className="text-lg font-bold text-amber-500">
                  {'★'.repeat(Math.round(qa.average_rating || 0))}{'☆'.repeat(5 - Math.round(qa.average_rating || 0))}
                  <span className="text-sm text-gray-600 ml-2">{qa.average_rating || 0} / 5</span>
                </p>
                {qa.rating_distribution && (
                  <div className="space-y-1">
                    {[5, 4, 3, 2, 1].map(r => {
                      const count = qa.rating_distribution[String(r)] || 0;
                      const pct = qa.total_answers > 0 ? Math.round((count / qa.total_answers) * 100) : 0;
                      return (
                        <div key={r} className="flex items-center gap-2 text-xs">
                          <span className="w-6 text-right text-gray-500">{r}★</span>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-8 text-gray-400">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {qa.type === 'yes_no' && (
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-bold text-lg">{qa.yes_count || 0}</p>
                    <p className="text-xs text-gray-500">Yes ({qa.total_answers > 0 ? Math.round(((qa.yes_count || 0) / qa.total_answers) * 100) : 0}%)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="font-bold text-lg">{qa.no_count || 0}</p>
                    <p className="text-xs text-gray-500">No ({qa.total_answers > 0 ? Math.round(((qa.no_count || 0) / qa.total_answers) * 100) : 0}%)</p>
                  </div>
                </div>
              </div>
            )}

            {qa.type === 'free_text' && qa.sample_answers && (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {qa.sample_answers.map((ans, ai) => (
                  <div key={ai} className="text-sm text-gray-700 bg-gray-50 rounded px-3 py-2 border-l-2 border-teal-300">
                    {ans}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
